import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlert, Zap } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, useToastStore } from '@/shared/ui';

import * as commitmentsApi from '../api/commitmentsApi';
import styles from './CloseButtons.module.css';
import type { CloseOutcome, CommitmentCloseReason, CommitmentSummary } from '../model/types';
import { commitmentKeys, useCloseCommitment } from '../queries/useCommitments';

/**
 * Para przycisków zamykających zobowiązanie — **dwie osie, nie jedna** (spec sek. 3).
 *
 * Wspólne dla panelu E1 i list z menu, bo to nie jest „ten sam wygląd", tylko **ta sama
 * reguła**: powód zamknięcia jest treścią decyzji, a nie metadanymi. `done` odpala zależne
 * Surty, `obsolete` je osierocia (decyzja 21) — dwie kopie tego kodu prędzej czy później
 * rozjechałyby się w tym, co pokazują po zamknięciu.
 *
 * ⛔ Nic nie renderuje, gdy `canClose` jest fałszywe. Decyzja 63 mówi wprost, że przy
 * cudzych zobowiązaniach akcji nie ma **i tak ma być** — uprawnienia liczy serwer, bo
 * własność bywa dziedziczona po `has` (spec 9.2).
 */
export function CloseButtons({ commitment }: { commitment: CommitmentSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const close = useCloseCommitment();
  const queryClient = useQueryClient();

  // ⚡ Krok pośredni z decyzji 93 (bezpiecznik P4): gdy domknięcie ma skutki u INNYCH ludzi
  // (obudzi albo osieroci czekające zobowiązania), człowiek widzi liczbę PRZED zatwierdzeniem.
  // Zwykłe zamknięcie — bez zależnych — przechodzi jak dotąd, jednym kliknięciem.
  const [confirming, setConfirming] = useState<{
    reason: CommitmentCloseReason;
    affected: number;
  } | null>(null);
  const [checking, setChecking] = useState(false);

  if (!commitment.canClose) {
    return null;
  }

  function doClose(reason: CommitmentCloseReason) {
    setConfirming(null);
    close.mutate(
      { id: commitment.id, reason, hostElementId: commitment.hostElementId },
      {
        onSuccess: (outcome) => show({ message: describeOutcome(outcome, t), tone: 'success' }),
        onError: (error) => show({ message: describeError(error), tone: 'danger' }),
      },
    );
  }

  async function onIntent(reason: CommitmentCloseReason) {
    setChecking(true);

    try {
      // `wouldFire` z detalu = odwrotne wyszukanie „kto na to czeka" (spec sek. 8, soczewka 1).
      // `staleTime: 0`, bo pytanie brzmi „co się stanie TERAZ" — jak przy podglądzie iteracji.
      const detail = await queryClient.fetchQuery({
        queryKey: commitmentKeys.detail(commitment.id),
        queryFn: ({ signal }) => commitmentsApi.fetchCommitment(commitment.id, signal),
        staleTime: 0,
      });

      if (detail.wouldFire.length === 0) {
        doClose(reason);
      } else {
        setConfirming({ reason, affected: detail.wouldFire.length });
      }
    } catch (error) {
      // ⛔ Bez podglądu nie zamykamy w ciemno — skoro bezpiecznik nie odpowiedział,
      // bezpieczniejsza jest odmowa z komunikatem niż domknięcie z niespodzianką.
      show({ message: describeError(error), tone: 'danger' });
    } finally {
      setChecking(false);
    }
  }

  if (confirming !== null) {
    return (
      <>
        {/* ⚡ / ⚠ były wpisane w treść tłumaczenia; od 2026-08-06 znak jest ELEMENTEM
            WIDOKU, a przekład niesie samo zdanie. Zysk poza porządkiem: ikona ma teraz
            barwę z warstwy semantycznej, więc odróżnia obudzenie od osierocenia także
            kolorem — czego glif w tekście nie umiał. */}
        <span className={styles['lead']}>
          {confirming.reason === 'done' ? (
            <Zap className={styles['fire']} size={15} aria-hidden="true" />
          ) : (
            <TriangleAlert className={styles['orphan']} size={15} aria-hidden="true" />
          )}

          {t(
            confirming.reason === 'done'
              ? 'commitments.closeConfirm.wouldFire'
              : 'commitments.closeConfirm.wouldOrphan',
            { count: confirming.affected },
          )}
        </span>

        <Button
          variant="ghost"
          disabled={close.isPending}
          onClick={() => doClose(confirming.reason)}
        >
          {t('commitments.closeConfirm.confirm')}
        </Button>

        <Button variant="ghost" disabled={close.isPending} onClick={() => setConfirming(null)}>
          {t('common.action.cancel')}
        </Button>
      </>
    );
  }

  const busy = close.isPending || checking;

  return (
    <>
      <Button variant="ghost" disabled={busy} onClick={() => void onIntent('done')}>
        {t('commitments.action.done')}
      </Button>

      <Button variant="ghost" disabled={busy} onClick={() => void onIntent('obsolete')}>
        {t('commitments.action.obsolete')}
      </Button>
    </>
  );
}

type Translate = (key: string, options: Record<string, unknown>) => string;

/**
 * ⭐ Skutki uboczne zamknięcia w jednym zdaniu.
 *
 * To **nie jest ozdoba komunikatu**. Zamknięcie jednego zadania budzi cudze zobowiązania
 * u innych ludzi — jeśli człowiek zamykający tego nie zobaczy, cała mechanika Surtów jest
 * dla niego niewidzialna i produkt wygląda jak zwykła lista zadań.
 */
function describeOutcome(outcome: CloseOutcome, t: Translate): string {
  const parts = [t('commitments.result.closed', { title: outcome.commitment.title })];

  if (outcome.fired.length > 0) {
    parts.push(t('commitments.result.fired', { count: outcome.fired.length }));
  }

  if (outcome.orphaned.length > 0) {
    parts.push(t('commitments.result.orphaned', { count: outcome.orphaned.length }));
  }

  return parts.join(' · ');
}
